// components/StaffPicker.tsx
// Shown as a full-screen overlay when rbac_enabled = true and no staff session exists.
// Step 1: select name + enter PIN
// Step 2 (if must_change_password): set a personal password before accessing the app

import { useEffect, useRef, useState } from "react";
import { Eye, EyeOff, Loader2 } from "lucide-react";
import { getAuthToken } from "../lib/supabase";
import { useStaff } from "../lib/StaffContext";
import type { ActiveStaff } from "../lib/StaffContext";

const API_URL = import.meta.env.VITE_API_URL as string;
const MAX_ATTEMPTS = 3;
const LOCKOUT_SECONDS = 300; // 5 minutes

interface StaffMember {
  staff_id: string;
  name: string;
  role: string | null;
  is_clinic_admin: boolean;
  is_active: boolean;
}

type Step = "login" | "change-password";

export default function StaffPicker() {
  const { setActiveStaff } = useStaff();

  // ── Step state ───────────────────────────────────────────────
  const [step, setStep] = useState<Step>("login");

  // ── Login step ───────────────────────────────────────────────
  const [members, setMembers] = useState<StaffMember[]>([]);
  const [loadingMembers, setLoadingMembers] = useState(true);
  const [selectedId, setSelectedId] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [attempts, setAttempts] = useState(0);
  const [lockedUntil, setLockedUntil] = useState<number | null>(null);
  const [remaining, setRemaining] = useState(0);
  const passwordRef = useRef<HTMLInputElement>(null);

  // ── Change-password step ─────────────────────────────────────
  // pendingStaff = verified staff waiting for password change before session is granted
  const [pendingStaff, setPendingStaff] = useState<(ActiveStaff & { must_change_password?: boolean }) | null>(null);
  const [currentPin, setCurrentPin] = useState(""); // the temp password they just entered
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const [changeError, setChangeError] = useState<string | null>(null);

  // ── Lockout countdown ────────────────────────────────────────
  useEffect(() => {
    if (!lockedUntil) return;
    const id = setInterval(() => {
      const left = Math.ceil((lockedUntil - Date.now()) / 1000);
      if (left <= 0) {
        setLockedUntil(null);
        setAttempts(0);
        setRemaining(0);
      } else {
        setRemaining(left);
      }
    }, 1000);
    return () => clearInterval(id);
  }, [lockedUntil]);

  // ── Load member list ─────────────────────────────────────────
  useEffect(() => {
    async function fetchMembers() {
      try {
        const token = await getAuthToken();
        const res = await fetch(`${API_URL}/clinic/members`, {
          headers: token ? { Authorization: token } : {},
        });
        if (!res.ok) throw new Error("Failed to load members");
        const json = await res.json();
        const active = (json.members as StaffMember[]).filter((m) => m.is_active);
        setMembers(active);
        if (active.length === 1) setSelectedId(active[0].staff_id);
      } catch {
        setError("Unable to load team members. Please refresh.");
      } finally {
        setLoadingMembers(false);
      }
    }
    fetchMembers();
  }, []);

  // ── Step 1: verify PIN ───────────────────────────────────────
  async function handleLogin(e: React.FormEvent) {
    e.preventDefault();
    if (lockedUntil) return;
    if (!selectedId) { setError("Please select your name."); return; }
    if (!password) { setError("Please enter your password."); return; }

    setSubmitting(true);
    setError(null);

    try {
      const token = await getAuthToken();
      const res = await fetch(`${API_URL}/clinic/staff/verify-pin`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: token } : {}),
        },
        body: JSON.stringify({ staff_id: selectedId, pin: password }),
      });

      if (res.ok) {
        const staff = await res.json() as ActiveStaff & { must_change_password?: boolean };
        if (staff.must_change_password) {
          // Keep the PIN in memory to re-verify on the change-password step
          setCurrentPin(password);
          setPendingStaff(staff);
          setPassword("");
          setStep("change-password");
        } else {
          setActiveStaff(staff);
        }
      } else {
        const newAttempts = attempts + 1;
        setAttempts(newAttempts);
        if (newAttempts >= MAX_ATTEMPTS) {
          const until = Date.now() + LOCKOUT_SECONDS * 1000;
          setLockedUntil(until);
          setError(`Too many incorrect attempts. Locked for ${LOCKOUT_SECONDS / 60} minutes.`);
        } else {
          const left = MAX_ATTEMPTS - newAttempts;
          setError(`Incorrect password. ${left} attempt${left > 1 ? "s" : ""} remaining.`);
        }
        setPassword("");
        passwordRef.current?.focus();
      }
    } catch {
      setError("Network error. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  // ── Step 2: change password ──────────────────────────────────
  async function handleChangePassword(e: React.FormEvent) {
    e.preventDefault();
    if (!pendingStaff) return;

    if (newPassword.length < 4) {
      setChangeError("Password must be at least 4 characters.");
      return;
    }
    if (newPassword !== confirmPassword) {
      setChangeError("Passwords do not match.");
      return;
    }

    setChangingPassword(true);
    setChangeError(null);

    try {
      const token = await getAuthToken();
      const res = await fetch(`${API_URL}/clinic/staff/change-password`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: token } : {}),
        },
        body: JSON.stringify({
          staff_id: pendingStaff.staff_id,
          current_pin: currentPin,
          new_password: newPassword,
        }),
      });

      if (res.ok) {
        const updatedStaff = await res.json() as ActiveStaff;
        setActiveStaff(updatedStaff);
      } else {
        const err = await res.json().catch(() => ({}));
        setChangeError((err as any).detail || "Failed to change password. Please try again.");
      }
    } catch {
      setChangeError("Network error. Please try again.");
    } finally {
      setChangingPassword(false);
    }
  }

  // ── Render ───────────────────────────────────────────────────
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-white">
      <div className="w-full max-w-sm px-8 py-10">

        {step === "login" ? (
          <>
            <h1 className="text-2xl font-semibold text-gray-800 mb-1">Who are you?</h1>
            <p className="text-sm text-gray-500 mb-8">
              Select your name and enter your personal password to continue.
            </p>

            {loadingMembers ? (
              <div className="flex items-center gap-2 text-gray-500 text-sm">
                <Loader2 className="w-4 h-4 animate-spin" />
                Loading team members…
              </div>
            ) : members.length === 0 ? (
              <p className="text-sm text-red-500">
                No active team members found. Contact your administrator.
              </p>
            ) : (
              <form onSubmit={handleLogin} className="flex flex-col gap-4">
                {/* Member selector */}
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1.5 uppercase tracking-wide">
                    Team member
                  </label>
                  <select
                    value={selectedId}
                    onChange={(e) => { setSelectedId(e.target.value); setError(null); }}
                    disabled={!!lockedUntil}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm bg-white text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                  >
                    <option value="">Select your name</option>
                    {members.map((m) => (
                      <option key={m.staff_id} value={m.staff_id}>
                        {m.name}{m.role ? ` — ${m.role}` : ""}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Password input */}
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1.5 uppercase tracking-wide">
                    Password
                  </label>
                  <div className="relative">
                    <input
                      ref={passwordRef}
                      type={showPassword ? "text" : "password"}
                      value={password}
                      onChange={(e) => { setPassword(e.target.value); setError(null); }}
                      disabled={!!lockedUntil || submitting}
                      placeholder="Enter your password"
                      autoComplete="current-password"
                      className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm pr-10 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((v) => !v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      tabIndex={-1}
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                {error && (
                  <p className="text-sm text-red-500">
                    {lockedUntil ? `Account locked. Try again in ${remaining}s.` : error}
                  </p>
                )}

                <button
                  type="submit"
                  disabled={submitting || !!lockedUntil}
                  className="mt-2 w-full bg-blue-600 text-white rounded-lg py-2.5 text-sm font-medium hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
                  {submitting ? "Verifying…" : "Sign in"}
                </button>
              </form>
            )}
          </>
        ) : (
          <>
            <h1 className="text-2xl font-semibold text-gray-800 mb-1">Set your password</h1>
            <p className="text-sm text-gray-500 mb-8">
              You logged in with a temporary password. Please choose a personal password before continuing.
            </p>

            <form onSubmit={handleChangePassword} className="flex flex-col gap-4">
              {/* New password */}
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1.5 uppercase tracking-wide">
                  New password
                </label>
                <div className="relative">
                  <input
                    type={showNew ? "text" : "password"}
                    value={newPassword}
                    onChange={(e) => { setNewPassword(e.target.value); setChangeError(null); }}
                    disabled={changingPassword}
                    placeholder="Choose a password"
                    autoComplete="new-password"
                    autoFocus
                    className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm pr-10 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                  />
                  <button
                    type="button"
                    onClick={() => setShowNew((v) => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    tabIndex={-1}
                  >
                    {showNew ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              {/* Confirm password */}
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1.5 uppercase tracking-wide">
                  Confirm password
                </label>
                <div className="relative">
                  <input
                    type={showConfirm ? "text" : "password"}
                    value={confirmPassword}
                    onChange={(e) => { setConfirmPassword(e.target.value); setChangeError(null); }}
                    disabled={changingPassword}
                    placeholder="Repeat your password"
                    autoComplete="new-password"
                    className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm pr-10 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirm((v) => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    tabIndex={-1}
                  >
                    {showConfirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              {changeError && (
                <p className="text-sm text-red-500">{changeError}</p>
              )}

              <button
                type="submit"
                disabled={changingPassword}
                className="mt-2 w-full bg-blue-600 text-white rounded-lg py-2.5 text-sm font-medium hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {changingPassword && <Loader2 className="w-4 h-4 animate-spin" />}
                {changingPassword ? "Saving…" : "Set password & continue"}
              </button>

              <button
                type="button"
                onClick={() => { setStep("login"); setNewPassword(""); setConfirmPassword(""); setChangeError(null); }}
                className="text-sm text-gray-400 hover:text-gray-600 text-center"
              >
                ← Back
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
